// 課程查詢（course-clazz-query-list）的頁面級 modal 群與列動作。
//
// legacy 對照（偵查報告行號）：
//   - 名稱連結 → 課程資訊 modal（clazzQueryList.js:131-139 → :485-508，唯讀、僅「關閉」）
//   - 點名欄 → 課堂點名 modal（rollCallModal.jsp:175-182；radio 到課/未到課/未點名/退選＋
//     上課表現＋備註；全部到課/全部未到課 :458-461、產生點名碼 :462-463、列印點名表 :464-465、
//     存檔 :471-472；關閉前 dirty guard「尚未存檔，是否確定要關閉?」:209-224）
//   - 報到 modal（clazzQueryList.jsp:490-526；點名碼 maxlength 50、「點名代碼長度不足6碼!」
//     clazzQueryList.js:246）
//   - 報名/退選（clazzQueryList.js:748/:750 $.confirm「是否確定要報名?」/「是否確定要退選?」）
//   - 表單明細 modal（clazzQueryFormModal.jsp:89-107；手動取單「是否確定要發表單給自己?」:131）
//   - 課堂公告 modal（clazzAnnouncementListModal.jsp:493-495；新增/修改存檔、內容空
//     alert「請填寫公告內容」、刪除 confirm「是否確定要刪除?」）
//
// 雛形資料層：attendance／announcements／rollCallCode／forms 全部存在**課程列本身**
// （resource-rules 種子＋localStorage 持久化），寫入一律走既有 update()（D-1 五個 op 之內）。
// 列印點名表／表單開啟等「下載/開新頁」動作在雛形以提示呈現（同 B1-3 模式，不帶 data-effect）。
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { Textarea } from '@/components/base/Textarea';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import type { Row } from '@/mocks/types';
import { useNavigate } from 'react-router-dom';
import { legacyAlertFallback, type BehaviorFlowContext } from '../templates/page-behaviors';

/** 點名狀態四值（rollCallModal.jsp columns radio；constant.js:121-126）。 */
const ROLLCALL_STATUSES: readonly string[] = ['到課', '未到課', '未點名', '退選'];

/** 學員列（row.attendance 的元素形狀）。 */
interface AttendanceEntry {
  readonly studentId: string;
  readonly name: string;
  status: string;
  score: string;
  reason: string;
}

function attendanceOf(row: Row): AttendanceEntry[] {
  const raw = row['attendance'];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((entry) => {
    const record = entry as Record<string, unknown>;
    return {
      studentId: String(record['studentId'] ?? ''),
      name: String(record['name'] ?? ''),
      status: String(record['status'] ?? '未點名'),
      score: String(record['score'] ?? ''),
      reason: String(record['reason'] ?? ''),
    };
  });
}

/** 確定性 6 位點名碼（legacy 產生點名碼由後端亂數；雛形以 id 雜湊產生，不用 Math.random）。 */
function rollCallCodeOf(seed: string): string {
  let hash = 0;
  for (const ch of seed) {
    hash = (hash * 31 + ch.charCodeAt(0)) % 1000003;
  }
  return String(100000 + (hash % 900000));
}

// ---------------------------------------------------------------------------
// 課堂點名 modal
// ---------------------------------------------------------------------------

/**
 * 點名面板（rollCallTab.jsp + rollCallTab.js 的共用本體；RollCallModal 與
 * 課程大 modal 課堂點名分頁共用，批次 11 抽取；uiId 維持 `${pageId}-rollcall-*`）。
 *
 * 批次 11（B2-4）補齊：scoreCheck「是否需要上課評分」開關（rollCallTab.jsp:49-50，
 * 控制上課表現欄可見＋updateRequireScoreAjax :34-45）與「取消產生點名碼」checkbox
 * （rollCallTab.jsp:55＋doDeleteRollCallCodeAjax :55-61）；碼顯示格式照 legacy
 * 「取消產生點名碼」換行「 點名代碼:」＋碼（rollCallTab.js:27-28/:158-159）。
 * 產生點名碼仍為確定性雜湊（非後端亂數，B2-4 既有決策）。
 */
export function RollCallPanel({
  pageId,
  row,
  onSaved,
  /** 存檔函式曝露給呼叫端（wrapper 的 footer 存檔鈕用）。 */
  saveRef,
  /** dirty 狀態回呼（wrapper 的關閉守衛用；legacy rollCallModal.jsp:209-224）。 */
  onDirtyChange,
}: {
  pageId: string;
  row: Row;
  /** 存檔／寫回後回呼（呼叫端關閉 modal 或標記 rollCallCompleted）。 */
  onSaved?(): void;
  saveRef?: { current: (() => void) | null };
  onDirtyChange?: (dirty: boolean) => void;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [entries, setEntries] = useState<AttendanceEntry[]>(attendanceOf(row));
  const [initial] = useState<AttendanceEntry[]>(attendanceOf(row));
  const [requireScore, setRequireScore] = useState(String(row['requireScore'] ?? '0'));
  // 產生點名碼後就地顯示（rollCallModal.jsp:137-143：產生後 modal 不關，重新載入表列）。
  const [localCode, setLocalCode] = useState<string | null>(null);
  const [codeDeleted, setCodeDeleted] = useState(false);
  const dirty = useMemo(
    () => JSON.stringify(entries) !== JSON.stringify(initial),
    [entries, initial],
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  const rowKey = String(row.id ?? '');
  const code = codeDeleted ? '' : (localCode ?? String(row['rollCallCode'] ?? ''));

  const save = () => {
    void (async () => {
      try {
        await dialogs.processing.wrap(
          dataSource.update(pageId, rowKey, { attendance: entries, requireScore }),
        );
        onSaved?.();
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };
  if (saveRef !== undefined) {
    saveRef.current = save;
  }

  const scoreColumns: readonly TableColumn[] = [
    { key: 'studentId', header: '學員編號' },
    { key: 'name', header: '學員姓名' },
    { key: 'status', header: '狀態' },
    ...(requireScore === '1' ? [{ key: 'score', header: '上課表現' } as TableColumn] : []),
    { key: 'reason', header: '備註' },
  ];

  return (
    <div data-ui-id={`${pageId}-rollcall-form`} className="space-y-3">
      <div data-ui-id={`${pageId}-rollcall-actions`} className="flex flex-wrap items-center gap-2">
        {/* rollCallTab.jsp:49-50 scoreCheck（B2-4）：勾選顯示上課表現欄＋updateRequireScore（:34-45）。 */}
        <label className="inline-flex items-center gap-1 text-sm">
          <input
            data-ui-id={`${pageId}-rollcall-score-check`}
            type="checkbox"
            checked={requireScore === '1'}
            onChange={(event) => {
              const next = event.target.checked ? '1' : '0';
              setRequireScore(next);
              void (async () => {
                try {
                  // legacy POST course/rollCall/updateRequireScoreAjax（rollCallTab.js:267-272）。
                  await dataSource.update(pageId, rowKey, { requireScore: next });
                } catch (cause: unknown) {
                  await dialogs.alert(legacyAlertFallback(cause));
                }
              })();
            }}
          />
          是否需要上課評分
        </label>
        {/* rollCallTab.jsp:63/:66 順序：全部未到課（2）→全部到課（1）（legacy 按鈕列順序）；
            雛形不跳過退選列（rollCallTab 差異）。 */}
        {(['未到課', '到課'] as const).map((status) => (
          <Button
            key={status}
            uiId={`${pageId}-rollcall-all-${status === '到課' ? 'yes' : 'no'}`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              setEntries((current) => current.map((entry) => ({ ...entry, status })));
            }}
          >
            全部{status}
          </Button>
        ))}
        {code === '' ? (
          <Button
            uiId={`${pageId}-rollcall-gen-code`}
            variant="default"
            size="sm"
            type="button"
            dataEffect={`update:${pageId}`}
            onClick={() => {
              void (async () => {
                try {
                  const generated = rollCallCodeOf(rowKey);
                  await dialogs.processing.wrap(
                    dataSource.update(pageId, rowKey, { rollCallCode: generated }),
                  );
                  setCodeDeleted(false);
                  setLocalCode(generated);
                } catch (cause: unknown) {
                  await dialogs.alert(legacyAlertFallback(cause));
                }
              })();
            }}
          >
            產生點名碼
          </Button>
        ) : (
          <span data-ui-id={`${pageId}-rollcall-code`} className="text-sm">
            {/* rollCallTab.jsp:49-52（label#btn-delete-roll-code checkbox 在前）＋rollCallTab.js:27-28/
                :158-159 文字（「取消產生點名碼」換行「 點名代碼:」＋碼）。 */}
            <label className="inline-flex items-center gap-1">
              {/* 取消產生點名碼 checkbox（B2-4）→ doDeleteRollCallCodeAjax（rollCallTab.js:56）。 */}
              <input
                data-ui-id={`${pageId}-rollcall-delete-code`}
                type="checkbox"
                onChange={(event) => {
                  if (!event.target.checked) {
                    return;
                  }
                  void (async () => {
                    try {
                      await dialogs.processing.wrap(
                        dataSource.update(pageId, rowKey, { rollCallCode: '' }),
                      );
                      setCodeDeleted(true);
                      setLocalCode(null);
                    } catch (cause: unknown) {
                      await dialogs.alert(legacyAlertFallback(cause));
                    }
                  })();
                }}
              />
            </label>
            取消產生點名碼
            <br />
            {' '}點名代碼:
            <span className="text-danger font-bold">{code}</span>
          </span>
        )}
        <Button
          uiId={`${pageId}-rollcall-print`}
          variant="default"
          size="sm"
          type="button"
          // 列印點名表＝legacy fileDownload（rollCallModal.jsp:413-430）；雛形不落地檔案（B1-3 模式）。
          onClick={() => {
            void dialogs.info(t('export.notice'));
          }}
        >
          列印點名表
        </Button>
      </div>
      <Table
        uiId={`${pageId}-rollcall-table`}
        columns={scoreColumns}
        rows={entries.map((entry) => ({
          id: entry.studentId,
          studentId: entry.studentId,
          name: entry.name,
          status: entry.status,
          score: entry.score,
          reason: entry.reason,
        }))}
        rowKey={(tableRow) => String(tableRow['studentId'])}
        emptyText="查無符合的資料"
        renderCell={(tableRow, columnKey) => {
          const studentId = String(tableRow['studentId']);
          const entry = entries.find((item) => item.studentId === studentId);
          if (entry === undefined) {
            return '\u00a0';
          }
          if (columnKey === 'status') {
            return (
              <span className="inline-flex flex-wrap gap-2 text-sm">
                {ROLLCALL_STATUSES.map((status) => (
                  <label key={status} className="inline-flex items-center gap-1">
                    <input
                      type="radio"
                      name={`${pageId}-rollcall-status-${studentId}`}
                      data-ui-id={`${pageId}-rollcall-status-${studentId}-${toKebabLocal(status)}`}
                      checked={entry.status === status}
                      onChange={() => {
                        setEntries((current) =>
                          current.map((item) =>
                            item.studentId === studentId ? { ...item, status } : item,
                          ),
                        );
                      }}
                    />
                    {status}
                  </label>
                ))}
              </span>
            );
          }
          if (columnKey === 'score' || columnKey === 'reason') {
            return (
              <TextInput
                uiId={`${pageId}-rollcall-${columnKey}-${studentId}`}
                value={columnKey === 'score' ? entry.score : entry.reason}
                onChange={(next) => {
                  setEntries((current) =>
                    current.map((item) =>
                      item.studentId === studentId
                        ? columnKey === 'score'
                          ? { ...item, score: next }
                          : { ...item, reason: next }
                        : item,
                    ),
                  );
                }}
              />
            );
          }
          return String(tableRow[columnKey] ?? '');
        }}
      />
    </div>
  );
}

/**
 * 課堂點名 modal（rollCallModal.jsp；批次 11 抽取為 wrapper＋RollCallPanel 本體，
 * 本體另供課程大 modal 課堂點名分頁複用）。dirty guard＝rollCallModal.jsp:209-224。
 */
export function RollCallModal({
  pageId,
  row,
  onClose,
  onSaved,
}: {
  pageId: string;
  row: Row;
  onClose(): void;
  onSaved(): void;
}): ReactElement {
  const dialogs = useDialogs();
  const [dirty, setDirty] = useState(false);
  const saveRef = useRef<(() => void) | null>(null);
  const close = () => {
    void (async () => {
      if (dirty) {
        const confirmed = await dialogs.confirm('尚未存檔，是否確定要關閉?');
        if (!confirmed) {
          return;
        }
      }
      onClose();
    })();
  };
  return (
    <ModalFrame
      uiId={`${pageId}-rollcall-dialog`}
      open
      title="課堂點名"
      size="lg"
      onClose={close}
      footer={
        <>
          <Button
            uiId={`${pageId}-rollcall-dialog-cancel`}
            variant="default"
            size="sm"
            type="button"
            onClick={close}
          >
            {t('action.close')}
          </Button>
          <Button
            uiId={`${pageId}-rollcall-dialog-save`}
            variant="primary"
            size="sm"
            type="button"
            dataEffect={`update:${pageId}`}
            onClick={() => {
              saveRef.current?.();
            }}
          >
            {t('action.save')}
          </Button>
        </>
      }
    >
      <RollCallPanel pageId={pageId} row={row} onSaved={onSaved} saveRef={saveRef} onDirtyChange={setDirty} />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 報到 modal（clazzQueryList.jsp:490-526）
// ---------------------------------------------------------------------------

export function CheckInModal({
  pageId,
  row,
  onClose,
  onSaved,
}: {
  pageId: string;
  row: Row;
  onClose(): void;
  /** 報到成功後回呼（呼叫端 reload 列表）。 */
  onSaved(): void;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const rowKey = String(row.id ?? '');

  const submit = () => {
    void (async () => {
      if (code.trim().length < 6) {
        // clazzQueryList.js:246 逐字。
        await dialogs.alert('點名代碼長度不足6碼!');
        return;
      }
      setBusy(true);
      try {
        if (code.trim() === String(row['rollCallCode'] ?? '')) {
          // 雛形語意：碼相符＝報到成功（更新報名狀態）；legacy 由後端比對後回 desc。
          await dialogs.processing.wrap(dataSource.update(pageId, rowKey, { status2: '已報名' }));
          // 先關 modal 再提示（與 legacy「關 modal＋showInfoDialog」同效果，測試可觀察）。
          onClose();
          onSaved();
          await dialogs.info(t('state.saveSuccess'));
        } else {
          await dialogs.alert(t('error.generic'));
        }
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <ModalFrame
      uiId={`${pageId}-checkin-dialog`}
      open
      title="報到"
      size="md"
      onClose={onClose}
      footer={
        <Button
          uiId={`${pageId}-checkin-dialog-ok`}
          variant="primary"
          size="sm"
          type="button"
          disabled={busy}
          dataEffect={`update:${pageId}`}
          onClick={submit}
        >
          報到
        </Button>
      }
    >
      <div data-ui-id={`${pageId}-checkin-form`} className="flex items-center gap-2">
        <label className="text-sm" htmlFor={`${pageId}-checkin-code`}>
          點名碼：
        </label>
        <TextInput
          uiId={`${pageId}-checkin-code`}
          value={code}
          onChange={setCode}
          readOnly={busy}
        />
      </div>
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 課程資訊 modal（唯讀，clazzQueryList.jsp:229-547）
// ---------------------------------------------------------------------------

const COURSE_INFO_FIELDS: readonly { readonly key: string; readonly label: string }[] = [
  { key: 'title', label: '課程名稱' },
  { key: 'id', label: '課程代碼' },
  { key: 'category2', label: '類型' },
  { key: 'category', label: '訓練類別' },
  { key: 'unit', label: '部門' },
  { key: 'category3', label: '課程類別' },
  { key: 'person', label: '授課教師' },
  { key: 'person2', label: '協同教師' },
  { key: 'score', label: '給予時數' },
  { key: 'date', label: '課程時間' },
  { key: 'unit2', label: '地點' },
];

export function CourseInfoModal({
  pageId,
  row,
  onClose,
}: {
  pageId: string;
  row: Row;
  onClose(): void;
}): ReactElement {
  return (
    <ModalFrame
      uiId={`${pageId}-course-info-dialog`}
      open
      title="課程資訊"
      size="lg"
      onClose={onClose}
      footer={
        <Button
          uiId={`${pageId}-course-info-dialog-cancel`}
          variant="default"
          size="sm"
          type="button"
          onClick={onClose}
        >
          {t('action.close')}
        </Button>
      }
    >
      <div data-ui-id={`${pageId}-course-info`} className="space-y-1 text-sm">
        {COURSE_INFO_FIELDS.map((field) => (
          <div key={field.key} data-ui-id={`${pageId}-course-info-${field.key}`}>
            <span className="inline-block w-24 font-medium">{field.label}：</span>
            {String(row[field.key] ?? '')}
          </div>
        ))}
      </div>
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 表單明細 modal（clazzQueryFormModal.jsp）
// ---------------------------------------------------------------------------

interface FormEntry {
  readonly student: string;
  readonly name: string;
  readonly dueDate: string;
  readonly flow: string;
  readonly status: string;
  readonly version: string;
  // 批次 46（FRM-001，Gitea issue #43）：showFill 導航帶參（clazzQueryFormModal.jsp:71-76
  // 點 .showFill → showFillForm(row.formKey, row.formTemplateId, row.formTemplateVersionId,
  // row.refTable, row.refId)）。種子值【雛形值待實機校準】。
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly formTemplateVersionId: string;
  readonly refTable: string;
  readonly refId: string;
}

function formsOf(row: Row): FormEntry[] {
  const raw = row['forms'];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((entry) => {
    const record = entry as Record<string, unknown>;
    return {
      student: String(record['student'] ?? ''),
      name: String(record['name'] ?? ''),
      dueDate: String(record['dueDate'] ?? ''),
      flow: String(record['flow'] ?? ''),
      status: String(record['status'] ?? ''),
      version: String(record['version'] ?? ''),
      formKey: String(record['formKey'] ?? ''),
      formTemplateId: String(record['formTemplateId'] ?? ''),
      formTemplateVersionId: String(record['formTemplateVersionId'] ?? ''),
      refTable: String(record['refTable'] ?? ''),
      refId: String(record['refId'] ?? ''),
    };
  });
}

/**
 * 表單明細表列鍵（tr／連結的 data-ui-id 延伸；§10.1 kebab）：學員名 kebab 化＋序號。
 * 批次 46（FRM-001）自 inline rowKey 抽出共用：名稱連結 uiId 也要同一把 key。
 * 學員名為中文（kebab 化後為空字串）時退回 'student' 前綴——避免組出 '--' 連續連字號
 * （§10.1／check-ui-ids.ts UI_ID_PATTERN /^[a-z0-9]+(-[a-z0-9]+)*$/ 不允許）。
 */
function formListRowKey(student: string, index: number): string {
  const kebab = toKebabLocal(student);
  return `${kebab === '' ? 'student' : kebab}-${String(index)}`;
}

export function FormListModal({
  pageId,
  row,
  onClose,
}: {
  pageId: string;
  row: Row;
  onClose(): void;
}): ReactElement {
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const forms = formsOf(row);
  const columns: readonly TableColumn[] = [
    { key: 'student', header: '學生' },
    { key: 'name', header: '表單名稱' },
    { key: 'dueDate', header: '應完成日期' },
    { key: 'flow', header: '表單流程' },
    { key: 'status', header: '表單狀態' },
    { key: 'version', header: '表單版本' },
  ];
  return (
    <ModalFrame
      uiId={`${pageId}-form-list-dialog`}
      open
      title="表單明細"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button
            uiId={`${pageId}-form-list-dialog-manual`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              // clazzQueryFormModal.jsp:131 手動取單 confirm → saveFormForStudentAjax；
              // 雛形不發真表單，以提示呈現（B1-3 模式）。
              void (async () => {
                const confirmed = await dialogs.confirm('是否確定要發表單給自己?');
                if (!confirmed) {
                  return;
                }
                await dialogs.info(t('state.saveSuccess'));
              })();
            }}
          >
            手動取單
          </Button>
          <Button
            uiId={`${pageId}-form-list-dialog-cancel`}
            variant="default"
            size="sm"
            type="button"
            onClick={onClose}
          >
            {t('action.close')}
          </Button>
        </>
      }
    >
      <Table
        uiId={`${pageId}-form-list-table`}
        columns={columns}
        rows={forms.map((entry, index) => ({ ...entry, uiKey: formListRowKey(entry.student, index) }))}
        rowKey={(tableRow) => String(tableRow['uiKey'] ?? '')}
        emptyText="查無符合的資料"
        renderCell={(tableRow, columnKey) => {
          if (columnKey === 'name') {
            // 批次 46（FRM-001，Gitea issue #43）：表單名稱＝showFill 連結
            //（clazzQueryFormModal.jsp:11-14 a.showEdit；點擊 :71-76 → showFillForm）。
            // formList.js:113-122 window.open('form/prompt/fillForm?isWindowOpen=true'
            //   + '&formKey=' + encodeURIComponent(formkey) + '&fromPage=classForm'
            //   + '&refTable=' + refTable + '&refId=' + refId) —— 站內導航鏡像
            //（D19-2／批次 45 先例 FormQueryBodies.openForm 同款，不真的開新視窗）。
            // URLSearchParams 鍵序照 legacy 串接順序；encodeURIComponent 只作用 formKey
            //（照 :117），refTable/refId legacy 為裸字串、種子為 ASCII 值零差異。
            const formKey = String(tableRow['formKey'] ?? '');
            if (formKey === '') {
              // 無 formKey（種子未帶鍵）＝維持純文字，不 invent 導流行為。
              return String(tableRow['name'] ?? '');
            }
            const params = new URLSearchParams({
              isWindowOpen: 'true',
              formKey,
              fromPage: 'classForm',
              refTable: String(tableRow['refTable'] ?? ''),
              refId: String(tableRow['refId'] ?? ''),
            });
            return (
              <button
                type="button"
                className="cursor-pointer text-[#1ab394] underline"
                data-ui-id={`${pageId}-form-list-table-row-${String(tableRow['uiKey'] ?? '')}-form-link`}
                onClick={() => {
                  navigate(`/form/prompt/fillForm?${params.toString()}`);
                }}
              >
                {String(tableRow['name'] ?? '')}
              </button>
            );
          }
          return String(tableRow[columnKey] ?? '');
        }}
      />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 課堂公告 modal（clazzAnnouncementListModal.jsp）
// ---------------------------------------------------------------------------

interface AnnouncementEntry {
  readonly subject: string;
  readonly date: string;
  readonly status: string;
}

function announcementsOf(row: Row): AnnouncementEntry[] {
  const raw = row['announcements'];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((entry) => {
    const record = entry as Record<string, unknown>;
    return {
      subject: String(record['subject'] ?? ''),
      date: String(record['date'] ?? ''),
      status: String(record['status'] ?? '一般'),
    };
  });
}

export function AnnouncementModal({
  pageId,
  row,
  onClose,
  onSaved,
}: {
  pageId: string;
  row: Row;
  onClose(): void;
  onSaved(): void;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const list = announcementsOf(row);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const rowKey = String(row.id ?? '');

  const saveNew = () => {
    void (async () => {
      if (content.trim() === '') {
        // clazzAnnouncementListModal.jsp:304 逐字。
        await dialogs.alert('請填寫公告內容');
        return;
      }
      try {
        await dialogs.processing.wrap(
          dataSource.update(pageId, rowKey, {
            announcements: [{ subject: subject.trim(), date: '2026/01/05', status: '一般' }, ...list],
          }),
        );
        setCreating(false);
        setSubject('');
        setContent('');
        onSaved();
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };

  const remove = (subjectText: string) => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除?');
      if (!confirmed) {
        return;
      }
      try {
        await dialogs.processing.wrap(
          dataSource.update(pageId, rowKey, {
            announcements: list.filter((entry) => entry.subject !== subjectText),
          }),
        );
        onSaved();
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };

  return (
    <ModalFrame
      uiId={`${pageId}-announcement-dialog`}
      open
      title="課堂公告"
      size="lg"
      onClose={onClose}
      footer={
        creating ? (
          <>
            <Button
              uiId={`${pageId}-announcement-dialog-cancel`}
              variant="default"
              size="sm"
              type="button"
              onClick={() => {
                setCreating(false);
              }}
            >
              {t('action.close')}
            </Button>
            <Button
              uiId={`${pageId}-announcement-dialog-save`}
              variant="primary"
              size="sm"
              type="button"
              dataEffect={`update:${pageId}`}
              onClick={saveNew}
            >
              {t('action.save')}
            </Button>
          </>
        ) : (
          <>
            <Button
              uiId={`${pageId}-announcement-new`}
              variant="primary"
              size="sm"
              type="button"
              dataModalTrigger={`${pageId}-announcement-dialog`}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('action.add')}
            </Button>
            <Button
              uiId={`${pageId}-announcement-dialog-cancel`}
              variant="default"
              size="sm"
              type="button"
              onClick={onClose}
            >
              {t('action.close')}
            </Button>
          </>
        )
      }
    >
      {creating ? (
        <div data-ui-id={`${pageId}-announcement-form`} className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="w-20 text-sm" htmlFor={`${pageId}-announcement-subject`}>
              主旨：
            </label>
            <TextInput uiId={`${pageId}-announcement-subject`} value={subject} onChange={setSubject} />
          </div>
          <div className="flex items-start gap-2">
            <label className="w-20 text-sm" htmlFor={`${pageId}-announcement-content`}>
              內容：
            </label>
            <Textarea uiId={`${pageId}-announcement-content`} value={content} onChange={setContent} />
          </div>
        </div>
      ) : (
        <Table
          uiId={`${pageId}-announcement-table`}
          columns={[
            { key: 'subject', header: '主旨' },
            { key: 'date', header: '發佈日期' },
            { key: 'status', header: '公告狀態' },
            { key: 'actions', header: '' },
          ]}
          rows={list.map((entry) => ({ ...entry }))}
          rowKey={(_tableRow, index) => String(index)}
          emptyText="查無符合的資料"
          renderCell={(tableRow, columnKey) => {
            if (columnKey !== 'actions') {
              return String(tableRow[columnKey] ?? '');
            }
            // ui-id 用列序（主旨是中文，kebab 化會消失）；刪除依主旨過濾。
            const index = list.findIndex((entry) => entry.subject === String(tableRow['subject'] ?? ''));
            return (
              <Button
                uiId={`${pageId}-announcement-delete-${String(index)}`}
                variant="danger"
                size="sm"
                type="button"
                // 刪除真的走 update()（把公告從清單移除），補 data-effect（複驗 F3）。
                dataEffect={`update:${pageId}`}
                onClick={() => {
                  remove(String(tableRow['subject'] ?? ''));
                }}
              >
                {t('action.delete')}
              </Button>
            );
          }}
        />
      )}
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 狀態欄／公告欄／表單欄的列動作（點名/報到/報名/退選/公告/表單）
// ---------------------------------------------------------------------------

/** 狀態欄（點名）的列動作組：報名/退選＋報到＋點名。 */
export function ClassQueryStatusCell({
  row,
  ctx,
}: {
  row: Row;
  ctx: BehaviorFlowContext;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [rollCallOpen, setRollCallOpen] = useState(false);
  const rowKey = String(row.id ?? '');
  const kebab = toKebabLocal(rowKey);
  const status = String(row['status2'] ?? '未報名');

  const signUp = (target: '已報名' | '未報名') => {
    void (async () => {
      const confirmed = await dialogs.confirm(
        target === '已報名' ? '是否確定要報名?' : '是否確定要退選?',
      );
      if (!confirmed) {
        return;
      }
      try {
        await dialogs.processing.wrap(dataSource.update(ctx.pageId, rowKey, { status2: target }));
        ctx.reload();
        await dialogs.info(t('state.saveSuccess'));
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };

  return (
    <span
      data-ui-id={`${ctx.pageId}-row-${kebab}-status-actions`}
      className="inline-flex flex-wrap items-center gap-1"
    >
      {status === '已報名' ? (
        <Button
          uiId={`${ctx.pageId}-row-${kebab}-drop`}
          variant="default"
          size="sm"
          type="button"
          dataEffect={`update:${ctx.pageId}`}
          onClick={() => {
            signUp('未報名');
          }}
        >
          退選
        </Button>
      ) : (
        <Button
          uiId={`${ctx.pageId}-row-${kebab}-signup`}
          variant="default"
          size="sm"
          type="button"
          dataEffect={`update:${ctx.pageId}`}
          onClick={() => {
            signUp('已報名');
          }}
        >
          報名
        </Button>
      )}
      <Button
        uiId={`${ctx.pageId}-row-${kebab}-checkin`}
        variant="default"
        size="sm"
        type="button"
        dataModalTrigger={`${ctx.pageId}-checkin-dialog`}
        onClick={() => {
          setCheckInOpen(true);
        }}
      >
        報到
      </Button>
      <Button
        uiId={`${ctx.pageId}-row-${kebab}-rollcall`}
        variant="default"
        size="sm"
        type="button"
        dataModalTrigger={`${ctx.pageId}-rollcall-dialog`}
        onClick={() => {
          setRollCallOpen(true);
        }}
      >
        點名
      </Button>
      {checkInOpen ? (
        <CheckInModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setCheckInOpen(false);
          }}
          onSaved={() => {
            ctx.reload();
          }}
        />
      ) : null}
      {rollCallOpen ? (
        <RollCallModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setRollCallOpen(false);
          }}
          onSaved={() => {
            setRollCallOpen(false);
            ctx.reload();
          }}
        />
      ) : null}
    </span>
  );
}

/** 名稱欄：連結開「課程資訊」唯讀 modal（不是修改 modal）。 */
export function ClassQueryTitleCell({
  row,
  ctx,
}: {
  row: Row;
  ctx: BehaviorFlowContext;
}): ReactElement {
  const [infoOpen, setInfoOpen] = useState(false);
  const kebab = toKebabLocal(String(row.id ?? ''));
  return (
    <>
      {/* clazzQueryList.js:131-139 .showEdit → showClass → 課程資訊 modal。 */}
      <button
        type="button"
        data-ui-id={`${ctx.pageId}-result-row-${kebab}-title-link`}
        className={`${LINK_TEXT_CLASS} cursor-pointer`}
        data-modal-trigger={`${ctx.pageId}-course-info-dialog`}
        onClick={() => {
          setInfoOpen(true);
        }}
      >
        {String(row['title'] ?? '')}
      </button>
      {infoOpen ? (
        <CourseInfoModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setInfoOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/** 公告欄／表單欄：開對應 modal 的按鈕。 */
export function ClassQueryModalButton({
  row,
  ctx,
  columnKey,
  label,
  dialogId,
}: {
  row: Row;
  ctx: BehaviorFlowContext;
  columnKey: 'announcement' | 'forms';
  label: string;
  dialogId: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const kebab = toKebabLocal(String(row.id ?? ''));
  return (
    <>
      <Button
        uiId={`${ctx.pageId}-row-${kebab}-${columnKey}-button`}
        variant="default"
        size="sm"
        type="button"
        dataModalTrigger={`${ctx.pageId}-${dialogId}`}
        onClick={() => {
          setOpen(true);
        }}
      >
        {label}
      </Button>
      {open && columnKey === 'announcement' ? (
        <AnnouncementModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setOpen(false);
          }}
          onSaved={() => {
            ctx.reload();
          }}
        />
      ) : null}
      {open && columnKey === 'forms' ? (
        <FormListModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/** data-ui-id 用的 kebab 化（同 shared.toUiKey 規則）。 */
export function toKebabLocal(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
}
